import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DatabaseService } from "../services/database";
// import { universities } from "../data/universities"; // Replaced with database colleges

interface EssaySettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (essayType: "common-app" | "supplemental", prompt: string) => void;
  currentPrompt: string;
  currentCollegeName?: string;
  essayId?: string;
}

// Common App prompts for 2024-2025
const COMMON_APP_PROMPTS = [
  {
    id: "prompt1",
    text: "Some students have a background, identity, interest, or talent that is so meaningful they believe their application would be incomplete without it. If this sounds like you, then please share your story.",
  },
  {
    id: "prompt2",
    text: "The lessons we take from obstacles we encounter can be fundamental to later success. Recount a time when you faced a challenge, setback, or failure. How did it affect you, and what did you learn from the experience?",
  },
  {
    id: "prompt3",
    text: "Reflect on a time when you questioned or challenged a belief or idea. What prompted your thinking? What was the outcome?",
  },
  {
    id: "prompt4",
    text: "Reflect on something that someone has done for you that has made you happy or thankful in a surprising way. How has this gratitude affected or motivated you?",
  },
  {
    id: "prompt5",
    text: "Discuss an accomplishment, event, or realization that sparked a period of personal growth and a new understanding of yourself or others.",
  },
  {
    id: "prompt6",
    text: "Describe a topic, idea, or concept you find so engaging that it makes you lose all track of time. Why does it captivate you? What or who do you turn to when you want to learn more?",
  },
  {
    id: "prompt7",
    text: "Share an essay on any topic of your choice. It can be one you've already written, one that responds to a different prompt, or one of your own design.",
  },
];

// Helper to determine initial essay type from props
const getInitialEssayType = (
  currentPrompt: string,
  currentCollegeName?: string
): "common-app" | "supplemental" => {
  if (!currentPrompt) return "common-app";
  const isCommonApp = COMMON_APP_PROMPTS.some((p) => p.text === currentPrompt);
  if (isCommonApp) return "common-app";
  if (currentCollegeName) return "supplemental";
  return "common-app";
};

// Helper to get initial Common App prompt ID
const getInitialCommonAppPromptId = (currentPrompt: string): string => {
  const commonAppPrompt = COMMON_APP_PROMPTS.find(
    (p) => p.text === currentPrompt
  );
  return commonAppPrompt?.id || "";
};

const EssaySettingsModal: React.FC<EssaySettingsModalProps> = ({
  open,
  onOpenChange,
  onSave,
  currentPrompt,
  currentCollegeName,
  essayId,
}) => {
  const [essayType, setEssayType] = useState<"common-app" | "supplemental">("common-app");
  const [selectedPrompt, setSelectedPrompt] = useState<string>("");
  const [selectedCollege, setSelectedCollege] = useState("");
  const [collegeSearch, setCollegeSearch] = useState("");
  const [showCollegeSuggestions, setShowCollegeSuggestions] = useState(false);
  const [supplementalData, setSupplementalData] = useState<any>(null);
  const [selectedSupplementalPrompt, setSelectedSupplementalPrompt] = useState("");
  const [loadingSupplemental, setLoadingSupplemental] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const [availableColleges, setAvailableColleges] = useState<string[]>([]);

  // Track previous open state to detect when modal opens
  const [prevOpen, setPrevOpen] = useState(false);
  // Track if we need to auto-select the prompt after fetching
  const [shouldAutoSelect, setShouldAutoSelect] = useState(false);
  // Store the prompt to auto-select (in case currentPrompt changes)
  const [promptToAutoSelect, setPromptToAutoSelect] = useState("");

  // Synchronously reset state when modal opens (before render completes)
  // This is React's official pattern for derived state that resets on prop changes
  if (open && !prevOpen) {
    setPrevOpen(true);

    const newEssayType = getInitialEssayType(currentPrompt, currentCollegeName);
    const newCommonAppPromptId = getInitialCommonAppPromptId(currentPrompt);

    setEssayType(newEssayType);
    setSelectedPrompt(newCommonAppPromptId);

    if (newEssayType === "supplemental" && currentCollegeName) {
      setSelectedCollege(currentCollegeName);
      setCollegeSearch(currentCollegeName);
      setLoadingSupplemental(true);
      // Mark that we should auto-select and store the prompt
      setShouldAutoSelect(true);
      setPromptToAutoSelect(currentPrompt);
    } else {
      setSelectedCollege("");
      setCollegeSearch("");
      setLoadingSupplemental(false);
      setShouldAutoSelect(false);
      setPromptToAutoSelect("");
    }

    setSelectedSupplementalPrompt("");
    setSupplementalData(null);
  }

  if (!open && prevOpen) {
    setPrevOpen(false);
  }

  // Load available colleges from universities table on mount
  useEffect(() => {
    const loadColleges = async () => {
      try {
        const colleges = await DatabaseService.getAllUniversityNames();
        setAvailableColleges(colleges);
      } catch (error) {
        console.error("Error loading available colleges:", error);
      }
    };
    loadColleges();
  }, []);

  // Update dropdown position when showing suggestions
  useEffect(() => {
    if (showCollegeSuggestions && inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  }, [showCollegeSuggestions]);

  // Handle clicks outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setShowCollegeSuggestions(false);
      }
    };

    if (showCollegeSuggestions) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showCollegeSuggestions]);

  // Fetch supplemental essay prompts when college is selected (from universities table)
  // Only fetch when selectedCollege changes, not when switching tabs
  useEffect(() => {
    const fetchSupplementalEssays = async () => {
      if (!selectedCollege) {
        setSupplementalData(null);
        return;
      }

      // Don't re-fetch if we already have data for this college
      if (supplementalData?.college_name === selectedCollege) {
        return;
      }

      setLoadingSupplemental(true);
      try {
        const universityData = await DatabaseService.getUniversity(selectedCollege);
        // Transform to expected format - universities table uses parsed_essays
        if (universityData?.parsed_essays?.length) {
          setSupplementalData({
            college_name: universityData.college_name,
            essay_prompts: universityData.parsed_essays.map((e: { prompt: string }) => e.prompt),
          });
        } else {
          setSupplementalData(null);
        }
      } catch (error) {
        console.error("Error fetching supplemental essays:", error);
        setSupplementalData(null);
      } finally {
        setLoadingSupplemental(false);
      }
    };

    if (selectedCollege) {
      fetchSupplementalEssays();
    }
  }, [selectedCollege]);

  // Auto-select the prompt after supplemental data is loaded
  useEffect(() => {
    if (!shouldAutoSelect || !promptToAutoSelect || !supplementalData?.essay_prompts?.length) {
      return;
    }

    // Comprehensive normalization: trim, normalize all whitespace, lowercase
    const normalizeString = (s: string) =>
      s.trim()
       .replace(/[\r\n\t]+/g, ' ')  // Replace newlines/tabs with space
       .replace(/\s+/g, ' ')         // Collapse multiple spaces
       .toLowerCase();               // Case-insensitive

    const normalizedPromptToMatch = normalizeString(promptToAutoSelect);

    // First try exact normalized match
    let matchingIndex = supplementalData.essay_prompts.findIndex(
      (prompt: string) => normalizeString(prompt) === normalizedPromptToMatch
    );

    // Fallback: check if one contains the other (first 100 chars)
    if (matchingIndex === -1) {
      const promptStart = normalizedPromptToMatch.substring(0, 100);
      matchingIndex = supplementalData.essay_prompts.findIndex(
        (prompt: string) => {
          const promptNormalized = normalizeString(prompt);
          return promptNormalized.startsWith(promptStart) ||
                 normalizedPromptToMatch.startsWith(promptNormalized.substring(0, 100));
        }
      );
    }

    // Additional fallback: check if prompt includes any essay_prompt or vice versa
    if (matchingIndex === -1) {
      matchingIndex = supplementalData.essay_prompts.findIndex(
        (prompt: string) => {
          const promptNormalized = normalizeString(prompt);
          return normalizedPromptToMatch.includes(promptNormalized) ||
                 promptNormalized.includes(normalizedPromptToMatch);
        }
      );
    }

    if (matchingIndex !== -1) {
      setSelectedSupplementalPrompt(String(matchingIndex));
    }

    // Clear the auto-select flag after attempting
    setShouldAutoSelect(false);
  }, [supplementalData, shouldAutoSelect, promptToAutoSelect]);

  const handleSave = () => {
    let promptToSave = "";

    if (essayType === "common-app" && selectedPrompt) {
      const prompt = COMMON_APP_PROMPTS.find((p) => p.id === selectedPrompt);
      if (prompt) {
        promptToSave = prompt.text;
      }
    } else if (
      essayType === "supplemental" &&
      selectedSupplementalPrompt &&
      supplementalData
    ) {
      const promptIndex = parseInt(selectedSupplementalPrompt);
      if (!isNaN(promptIndex) && supplementalData.essay_prompts[promptIndex]) {
        promptToSave = supplementalData.essay_prompts[promptIndex];
      }
    }

    if (promptToSave) {
      onSave(essayType, promptToSave);
      onOpenChange(false);
    }
  };

  const isValid =
    (essayType === "common-app" && selectedPrompt) ||
    (essayType === "supplemental" && selectedSupplementalPrompt);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px]">
        <DialogHeader>
          <DialogTitle>Essay Settings</DialogTitle>
          <DialogDescription>
            Update your essay type and prompt
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Essay Type */}
          <div className="space-y-2">
            <Label>Essay Type</Label>
            <div className="grid grid-cols-2 gap-4">
              <button
                type="button"
                onClick={() => {
                  setEssayType("common-app");
                }}
                className={`flex flex-col items-start gap-2 rounded-lg border-2 p-4 transition-all ${
                  essayType === "common-app"
                    ? "border-black bg-gray-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`h-4 w-4 rounded-full border-2 ${
                      essayType === "common-app"
                        ? "border-black bg-black"
                        : "border-gray-300"
                    }`}
                  >
                    {essayType === "common-app" && (
                      <div className="h-full w-full rounded-full bg-white scale-50" />
                    )}
                  </div>
                  <span className="font-semibold text-sm">Common App</span>
                </div>
                <p className="text-xs text-gray-600 text-left">
                  Personal statement for the Common Application
                </p>
              </button>

              <button
                type="button"
                onClick={() => {
                  setEssayType("supplemental");
                }}
                className={`flex flex-col items-start gap-2 rounded-lg border-2 p-4 transition-all ${
                  essayType === "supplemental"
                    ? "border-black bg-gray-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-2">
                  <div
                    className={`h-4 w-4 rounded-full border-2 ${
                      essayType === "supplemental"
                        ? "border-black bg-black"
                        : "border-gray-300"
                    }`}
                  >
                    {essayType === "supplemental" && (
                      <div className="h-full w-full rounded-full bg-white scale-50" />
                    )}
                  </div>
                  <span className="font-semibold text-sm">Supplemental</span>
                </div>
                <p className="text-xs text-gray-600 text-left">
                  College-specific supplemental essay
                </p>
              </button>
            </div>
          </div>

          {/* Common App Prompt Selection */}
          {essayType === "common-app" && (
            <div className="space-y-2">
              <Label htmlFor="prompt">Select Common App Prompt</Label>
              <select
                id="prompt"
                value={selectedPrompt}
                onChange={(e) => setSelectedPrompt(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Choose a prompt...</option>
                {COMMON_APP_PROMPTS.map((prompt) => (
                  <option key={prompt.id} value={prompt.id}>
                    {prompt.text.substring(0, 80)}...
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Supplemental - College Search and Prompt Selection */}
          {essayType === "supplemental" && (
            <div className="space-y-4">
              {/* College Search */}
              <div className="space-y-2">
                <Label htmlFor="college-search">Search College</Label>
                <div className="relative" ref={dropdownRef}>
                  <Input
                    id="college-search"
                    ref={inputRef}
                    value={collegeSearch}
                    onChange={(e) => {
                      setCollegeSearch(e.target.value);
                      setShowCollegeSuggestions(e.target.value.length > 0);
                      if (!e.target.value) {
                        setSelectedCollege("");
                      }
                    }}
                    placeholder="Type to search colleges..."
                    autoComplete="off"
                  />

                  {/* College Suggestions Dropdown using Portal */}
                  {showCollegeSuggestions &&
                    availableColleges
                      .filter((college) =>
                        college
                          .toLowerCase()
                          .includes(collegeSearch.toLowerCase())
                      )
                      .slice(0, 10).length > 0 &&
                    createPortal(
                      <div
                        className="fixed bg-white border border-gray-200 rounded-lg shadow-lg z-[100] max-h-60 overflow-y-auto"
                        style={{
                          top: `${dropdownPosition.top}px`,
                          left: `${dropdownPosition.left}px`,
                          width: `${dropdownPosition.width}px`,
                        }}
                      >
                        {availableColleges
                          .filter((college) =>
                            college
                              .toLowerCase()
                              .includes(collegeSearch.toLowerCase())
                          )
                          .slice(0, 10)
                          .map((college, index) => (
                            <button
                              key={index}
                              onClick={() => {
                                setSelectedCollege(college);
                                setCollegeSearch(college);
                                setShowCollegeSuggestions(false);
                              }}
                              className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 transition-colors"
                            >
                              {college}
                            </button>
                          ))}
                      </div>,
                      document.body
                    )}
                </div>
              </div>

              {/* Supplemental Prompts Selection - show immediately with loading state */}
              {selectedCollege && (
                <div className="space-y-2">
                  <Label>Select Supplemental Prompt</Label>
                  {loadingSupplemental ? (
                    <select
                      disabled
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 bg-gray-50 text-gray-500 cursor-wait"
                    >
                      <option>Loading prompts...</option>
                    </select>
                  ) : supplementalData ? (
                    <select
                      value={selectedSupplementalPrompt}
                      onChange={(e) =>
                        setSelectedSupplementalPrompt(e.target.value)
                      }
                      className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">Choose a prompt...</option>
                      {supplementalData.essay_prompts.map(
                        (prompt: string, index: number) => (
                          <option key={index} value={String(index)}>
                            {prompt.substring(0, 80)}...
                          </option>
                        )
                      )}
                    </select>
                  ) : (
                    <p className="text-sm text-gray-500 py-2">
                      No supplemental prompts found for this college.
                    </p>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={!isValid}>
            Save Settings
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EssaySettingsModal;
